import type {
  ArticleStatus,
  GenerationStage,
  DeliveryStatus,
  ResearchRunStatus,
  TenantSlug,
} from '@cockpit/api-contract'
import type { LucideIcon } from 'lucide-react'
import {
  CalendarClock,
  CheckCircle2,
  Clock,
  FileCheck,
  FileText,
  Globe,
  History,
  Languages,
  ListTree,
  Loader2,
  MinusCircle,
  PenLine,
  Search,
  ShieldCheck,
  ShieldX,
  UploadCloud,
  XCircle,
} from 'lucide-react'

export type StatusTone =
  'neutral' | 'info' | 'processing' | 'ready' | 'pending' | 'success' | 'danger' | 'warning'

export interface StatusMeta {
  tone: StatusTone
  icon: LucideIcon
  labelKey: string
  pulse?: boolean
}

/** Article lifecycle status → tone/icon/label. Exhaustive over ArticleStatus. */
export const ARTICLE_STATUS_META: Record<ArticleStatus, StatusMeta> = {
  draft: { tone: 'neutral', icon: FileText, labelKey: 'status.article.draft' },
  scheduled: { tone: 'info', icon: CalendarClock, labelKey: 'status.article.scheduled' },
  generating: {
    tone: 'processing',
    icon: Loader2,
    labelKey: 'status.article.generating',
    pulse: true,
  },
  generated: { tone: 'ready', icon: FileCheck, labelKey: 'status.article.generated' },
  publishing: {
    tone: 'pending',
    icon: UploadCloud,
    labelKey: 'status.article.publishing',
    pulse: true,
  },
  published: { tone: 'success', icon: Globe, labelKey: 'status.article.published' },
  failed: { tone: 'danger', icon: XCircle, labelKey: 'status.article.failed' },
  translating: {
    tone: 'processing',
    icon: Languages,
    labelKey: 'status.article.translating',
    pulse: true,
  },
  superseded: { tone: 'neutral', icon: History, labelKey: 'status.article.superseded' },
}

/** Research run status → tone/icon/label. Exhaustive over ResearchRunStatus. */
export const RESEARCH_RUN_STATUS_META: Record<ResearchRunStatus, StatusMeta> = {
  pending: { tone: 'pending', icon: Clock, labelKey: 'status.researchRun.pending' },
  running: {
    tone: 'processing',
    icon: Loader2,
    labelKey: 'status.researchRun.running',
    pulse: true,
  },
  completed: { tone: 'success', icon: CheckCircle2, labelKey: 'status.researchRun.completed' },
  failed: { tone: 'danger', icon: XCircle, labelKey: 'status.researchRun.failed' },
  skipped: { tone: 'neutral', icon: MinusCircle, labelKey: 'status.researchRun.skipped' },
}

/** Generation pipeline stage → tone/icon/label. Exhaustive over GenerationStage. */
export const GENERATION_STAGE_META: Record<GenerationStage, StatusMeta> = {
  ground: { tone: 'processing', icon: Search, labelKey: 'status.stage.ground', pulse: true },
  outline: { tone: 'processing', icon: ListTree, labelKey: 'status.stage.outline', pulse: true },
  draft: { tone: 'processing', icon: PenLine, labelKey: 'status.stage.draft', pulse: true },
  complete: { tone: 'ready', icon: CheckCircle2, labelKey: 'status.stage.complete' },
  failed: { tone: 'danger', icon: XCircle, labelKey: 'status.stage.failed' },
}

/** Webhook delivery status → tone/icon/label. Exhaustive over DeliveryStatus. */
export const DELIVERY_STATUS_META: Record<DeliveryStatus, StatusMeta> = {
  pending: { tone: 'pending', icon: Clock, labelKey: 'status.delivery.pending' },
  delivered: { tone: 'success', icon: CheckCircle2, labelKey: 'status.delivery.delivered' },
  failed: { tone: 'danger', icon: XCircle, labelKey: 'status.delivery.failed' },
}

/** Quality-gate outcome → tone/icon/label. */
export const QUALITY_META: Record<'pass' | 'fail', StatusMeta> = {
  pass: { tone: 'success', icon: ShieldCheck, labelKey: 'status.quality.pass' },
  fail: { tone: 'danger', icon: ShieldX, labelKey: 'status.quality.fail' },
}

/** Prebuilt Tailwind utility strings per tone — components never hand-roll these. */
export const TONE_CLASSES: Record<
  StatusTone,
  { soft: string; solid: string; fg: string; dot: string }
> = {
  neutral: {
    soft: 'bg-tone-neutral-soft text-tone-neutral-fg',
    solid: 'bg-tone-neutral text-background',
    fg: 'text-tone-neutral-fg',
    dot: 'bg-tone-neutral',
  },
  info: {
    soft: 'bg-tone-info-soft text-tone-info-fg',
    solid: 'bg-tone-info text-background',
    fg: 'text-tone-info-fg',
    dot: 'bg-tone-info',
  },
  processing: {
    soft: 'bg-tone-processing-soft text-tone-processing-fg',
    solid: 'bg-tone-processing text-background',
    fg: 'text-tone-processing-fg',
    dot: 'bg-tone-processing',
  },
  ready: {
    soft: 'bg-tone-ready-soft text-tone-ready-fg',
    solid: 'bg-tone-ready text-background',
    fg: 'text-tone-ready-fg',
    dot: 'bg-tone-ready',
  },
  pending: {
    soft: 'bg-tone-pending-soft text-tone-pending-fg',
    solid: 'bg-tone-pending text-background',
    fg: 'text-tone-pending-fg',
    dot: 'bg-tone-pending',
  },
  success: {
    soft: 'bg-tone-success-soft text-tone-success-fg',
    solid: 'bg-tone-success text-background',
    fg: 'text-tone-success-fg',
    dot: 'bg-tone-success',
  },
  danger: {
    soft: 'bg-tone-danger-soft text-tone-danger-fg',
    solid: 'bg-tone-danger text-background',
    fg: 'text-tone-danger-fg',
    dot: 'bg-tone-danger',
  },
  warning: {
    soft: 'bg-tone-warning-soft text-tone-warning-fg',
    solid: 'bg-tone-warning text-background',
    fg: 'text-tone-warning-fg',
    dot: 'bg-tone-warning',
  },
}

/** Brand identity metadata — colorVar/softVar point at --brand-* tokens. */
export const TENANT_META: Record<
  TenantSlug,
  { labelKey: string; colorVar: string; softVar: string }
> = {
  'alerte-immo': {
    labelKey: 'tenant.alerte-immo',
    colorVar: '--brand-alerte-immo',
    softVar: '--brand-alerte-immo-soft',
  },
  patronome: {
    labelKey: 'tenant.patronome',
    colorVar: '--brand-patronome',
    softVar: '--brand-patronome-soft',
  },
  reseaumobile: {
    labelKey: 'tenant.reseaumobile',
    colorVar: '--brand-reseaumobile',
    softVar: '--brand-reseaumobile-soft',
  },
}
